<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { extractError } from '@/api/client'
import Modal from '@/components/ui/Modal.vue'
import { useUserStore } from '@/stores/user'

const user = useUserStore()
const router = useRouter()

const saving = ref(false)
const activating = ref(false)
const enableOpen = ref(false)
const modalOpen = ref(false)
const code = ref('')
const error = ref('')

const enabled = computed(() => user.profile?.connects_from_russia === true)

function errorText(e: unknown, fallback: string): string {
  return extractError(e, fallback)
}

function openDisable() {
  code.value = ''
  error.value = ''
  modalOpen.value = true
}

function openEnable() {
  error.value = ''
  enableOpen.value = true
}

function closeEnable() {
  if (saving.value) return
  enableOpen.value = false
}

async function confirmEnable() {
  if (saving.value) return
  saving.value = true
  error.value = ''
  try {
    const res = await user.enableConnectsFromRussia()
    if (!res.success) {
      error.value = res.message || 'Не удалось сохранить'
      return
    }
    enableOpen.value = false
  } catch (e) {
    error.value = errorText(e, 'Не удалось сохранить')
  } finally {
    saving.value = false
  }
}

function onCheckboxClick() {
  if (enabled.value) {
    openDisable()
    return
  }
  openEnable()
}

async function activate() {
  const value = code.value.trim()
  if (!value || activating.value) return
  activating.value = true
  error.value = ''
  try {
    const res = await user.redeemConnectsFromRussia(value)
    if (!res.success) {
      error.value = res.message || 'Код недействителен'
      return
    }
    modalOpen.value = false
    code.value = ''
  } catch (e) {
    error.value = errorText(e, 'Код недействителен')
  } finally {
    activating.value = false
  }
}

function goToChat() {
  modalOpen.value = false
  void router.push({ name: 'support' })
}
</script>

<template>
  <div class="panel flex h-full flex-col gap-3 !py-4 !px-5">
    <h3 class="text-sm font-bold">Подключение</h3>
    <label class="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        class="h-4 w-4 rounded border-white-light text-primary"
        :checked="enabled"
        :disabled="saving || activating"
        @click.prevent="onCheckboxClick"
      />
      Подключение из России
    </label>
    <small v-if="error && !modalOpen && !enableOpen" class="text-xs text-danger">{{ error }}</small>
  </div>

  <Modal :open="enableOpen" title="Подключение из России" @close="closeEnable">
    <div class="space-y-4 text-sm leading-relaxed text-[#1f2937]">
      <p>При включении этой функции будут включены специальные маршруты для России.</p>
      <small v-if="error" class="block text-xs text-danger">{{ error }}</small>
    </div>
    <template #footer>
      <button type="button" class="btn btn-primary" :disabled="saving" @click="confirmEnable">
        {{ saving ? 'Сохранение…' : 'Подтвердить' }}
      </button>
      <button type="button" class="btn btn-outline-primary" :disabled="saving" @click="closeEnable">
        Отменить
      </button>
    </template>
  </Modal>

  <Modal :open="modalOpen" title="Подключение из России" @close="modalOpen = false">
    <div class="space-y-4 text-sm leading-relaxed text-[#1f2937]">
      <p>
        Чтобы отключить подключение из России, запросите код подтверждения у техподдержки в чате
        поддержки. Полученный код действует 24 часа.
      </p>
      <label class="block space-y-1">
        <span class="text-xs font-semibold text-[#6b7280]">Код подтверждения</span>
        <input
          v-model="code"
          type="text"
          class="form-input"
          autocomplete="off"
          spellcheck="false"
          :disabled="activating"
          @keydown.enter.prevent="activate"
        />
      </label>
      <small v-if="error" class="block text-xs text-danger">{{ error }}</small>
    </div>
    <template #footer>
      <button type="button" class="btn btn-primary" :disabled="activating || !code.trim()" @click="activate">
        {{ activating ? 'Проверка…' : 'Активировать' }}
      </button>
      <button type="button" class="btn btn-outline-primary" @click="goToChat">
        Перейти в чат
      </button>
    </template>
  </Modal>
</template>
